/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import { Progress } from "reactstrap";
import { FormattedMessage } from "react-intl";
import clsx from "clsx";
import Score from "./Score";
import styles from "./GenericProgressBar.scss";

const propTypes = {
  /**
   * Whether or not the progress bar has a single value, or multiple values
   */
  isMultiValueProgressBar: PropTypes.bool,
  /**
   * The title of above the progress bar
   */
  title: PropTypes.string,
  /**
   * The data to display in the progress bar. This should always be an array. If you are using a single progress bar with a fractional footer, place the endValue at the end of the array
   */
  values: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * When displaying a legend below the progress bar, this will its labels
   */
  valueLabels: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * Whether or not to display a percentage caret above the progress bar
   */
  showPercentage: PropTypes.bool,
  /**
   * Whether or not to display a score component above the progress bar
   */
  showScore: PropTypes.bool,
  /**
   * Whether or not to display a fractional count below the progress bar
   */
  showFractionalFooter: PropTypes.bool,
  /**
   * Whether or not to display a legend below the progress bar
   */
  showLegendFooter: PropTypes.bool,
  /**
   * Whether or not to display an even spread of 3 values below the progress bar (single progress bar reference the values definition above)
   */
  showThreeValueFooter: PropTypes.bool,
  /**
   * Same as showThreeValueFooter but display percentage icon
   */
  showThreeValueFooterAsPercentage: PropTypes.bool,
  /**
   * The colors of the value(s) in the progress bar
   */
  colors: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * The maximum value depth to traverse when displaying a percentage caret (When initial values are zero, we want to skip them, but this value ensures we do not display the caret over a "negative" label such as Not Registered, or Not Measured for example)
   */
  maxLabelDepth: PropTypes.number,
  /**
   * Style object to be applied on container
   */
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * Score label
   */
  scoreLabel: PropTypes.node,
  /**
   * If true, shows top label
   */
  showTopLabel: PropTypes.bool,
  /**
   * Top label
   */
  topLabelString: PropTypes.string,
  /**
   * If true, shows right label
   */
  showRightLabel: PropTypes.bool,
  /**
   * Right label
   */
  rightLabelString: PropTypes.string,
  /**
   * Starting value to be displayed in the footer
   */
  startingValue: PropTypes.node,
  /**
   * Specify what indicies to sum, as well as where to display the percentage
   */
  sumIndices: PropTypes.array, // eslint-disable-line react/forbid-prop-types
};

const defaultProps = {
  isMultiValueProgressBar: undefined,
  title: undefined,
  values: undefined,
  valueLabels: undefined,
  showPercentage: undefined,
  showScore: undefined,
  showFractionalFooter: undefined,
  showLegendFooter: undefined,
  showThreeValueFooter: undefined,
  showThreeValueFooterAsPercentage: undefined,
  colors: undefined,
  maxLabelDepth: undefined,
  style: undefined,
  scoreLabel: undefined,
  showTopLabel: undefined,
  topLabelString: undefined,
  showRightLabel: undefined,
  rightLabelString: undefined,
  startingValue: undefined,
  sumIndices: undefined,
};

/**
 * Renders different types of progress bars depending on the props
 */
function GenericProgressBar(props) {
  const {
    isMultiValueProgressBar,
    style,
    title,
    values,
    valueLabels,
    showPercentage,
    showScore,
    scoreLabel,
    showTopLabel,
    topLabelString,
    showRightLabel,
    rightLabelString,
    showFractionalFooter,
    showLegendFooter,
    showThreeValueFooter,
    showThreeValueFooterAsPercentage,
    startingValue,
    colors,
    maxLabelDepth,
    sumIndices,
  } = props;

  const [percentLabel, setPercentLabel] = useState({});

  // Handles setting up the percentage caret once all data is available
  useEffect(() => {
    if (showPercentage && values) {
      // eslint-disable-next-line no-use-before-define
      setPercentLabel(calculatePercentageLabel());
    }
  }, [values, maxLabelDepth, colors, sumIndices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Converts a provided value to a percentage value
  const convertValueToPercentage = (valueToConvert) => {
    let convertedValue = 0;
    if (typeof valueToConvert === "number" && valueToConvert !== 0) {
      convertedValue = 100 * (valueToConvert / 100);
    }
    return convertedValue;
  };

  // Converts a color to a background style
  const convertColorToStyle = (colorHex) => ({
    backgroundColor: colorHex,
  });

  // Calculates the styling of the percentage caret
  const calculatePercentageLabel = () => {
    const percentLabel = { margin: 0, value: 0 }; // eslint-disable-line no-shadow

    // Edgecase when multiple values are to be added up for percentages
    if (sumIndices) {
      for (let i = 0; i < sumIndices.length; i += 1) {
        const index = sumIndices[i];
        percentLabel.margin += values[index];
        percentLabel.value += Math.round(values[index]);
        percentLabel.color = "#969696"; // Set color to gray for sum
      }
      percentLabel.margin = convertValueToPercentage(percentLabel.margin);
      return percentLabel;
    }

    for (let i = 0; i < maxLabelDepth; i += 1) {
      percentLabel.margin = convertValueToPercentage(values[i]);
      percentLabel.color = colors[i];
      percentLabel.value = Math.round(values[i]);
      if (values[i] !== 0) {
        return percentLabel;
      }
    }
    return percentLabel;
  };

  return (
    <div className={styles["gpb-container"]} style={style}>
      {/* Title */}
      {title !== undefined && (
        <p className={clsx(styles["gpb-title"], styles["font-bold"])}>
          <FormattedMessage id={title} />
        </p>
      )}

      {values === null || values === undefined ? (
        <div className={styles["no-data-label"]}>
          <FormattedMessage id="GENERAL.NO_DATA" />
        </div>
      ) : (
        <>
          {/* Top labels */}

          {/* Score */}
          {showScore && values && values.length ? (
            <div>
              <Score
                color={colors[0]}
                numerator={values[0]}
                denominator={values[1]}
                scoreLabel={scoreLabel || ""}
              />
            </div>
          ) : showPercentage && percentLabel !== undefined ? (
            <>
              {/* Percentage Label */}
              <Row>
                <Col>
                  <div style={{ marginLeft: `${percentLabel?.margin}%` }}>
                    <p
                      style={{
                        color: percentLabel?.color,
                        marginLeft:
                          percentLabel?.value?.toString()?.length === 1
                            ? "-12px"
                            : percentLabel?.value?.toString()?.length === 2
                            ? "-16px"
                            : "-20px",
                      }}
                      className={clsx(
                        styles["gpb-percentage-label"],
                        styles["font-bold"]
                      )}
                    >
                      {`${percentLabel?.value}%`}
                    </p>
                    <div className={styles["gpb-caret"]}>
                      <i
                        className="fas fa-caret-down"
                        style={{ fontSize: "14px" }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          ) : showTopLabel && topLabelString !== undefined ? (
            <p className={styles["gpb-top-label"]}>{topLabelString}</p>
          ) : (
            <>
              {/* No labels */}
              <div style={{ marginTop: "54px" }} />
            </>
          )}

          {/* Progress bar */}
          <Row className="align-items-center">
            <Col>
              {!isMultiValueProgressBar ? (
                <div className={styles["gpb-bar-wrapper"]}>
                  {/* Single Value Progress Bar */}
                  <Progress
                    style={{
                      height: "8px",
                    }}
                    barStyle={{ ...convertColorToStyle(colors[0]) }}
                    value={(values[0] / values[1]) * 100}
                  />
                </div>
              ) : (
                <>
                  {/* Multi Value Progress Bar */}
                  <Progress
                    multi={isMultiValueProgressBar}
                    className={styles["gpb-bar-wrapper"]}
                    style={{
                      height: "8px",
                    }}
                  >
                    {values?.map((value, index) => (
                      <Progress
                        bar
                        key={index} // eslint-disable-line react/no-array-index-key
                        barStyle={{
                          ...convertColorToStyle(colors[index]),
                        }}
                        value={
                          values[values?.length - 1] // eslint-disable-line no-unsafe-optional-chaining
                            ? (value / values[values?.length - 1]) * 100 // eslint-disable-line no-unsafe-optional-chaining
                            : value
                        }
                      />
                    ))}
                  </Progress>
                </>
              )}
            </Col>

            {/* Label Right of Progress Bar */}
            {showRightLabel && (
              <p id={styles["gpb-right-label"]} className={styles["text-gray"]}>
                {rightLabelString}
              </p>
            )}
          </Row>

          {/* Footers */}

          {/* Legend */}
          {showLegendFooter && !showFractionalFooter && (
            <Row className="mt-2" style={{ minHeight: "0px" }}>
              <Col>
                <ul className="pl-0 mt-2">
                  {valueLabels?.map((valueLabel, index) => (
                    <span
                      className={clsx("mr-3", styles["gpb-value-label"])}
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                    >
                      <span
                        className={styles.bullet}
                        style={{ backgroundColor: colors[index] }}
                      />
                      <span className={styles["text-gray"]}>{valueLabel}</span>
                    </span>
                  ))}
                </ul>
              </Col>
            </Row>
          )}

          {/* Fractional Count */}
          {showFractionalFooter &&
            !showLegendFooter &&
            values &&
            values?.length > 1 && (
              <Row>
                <Col>
                  <div className={styles["gpb-fraction-footer"]}>
                    <span
                      className={clsx(
                        styles["text-dark-blue"],
                        styles["font-bold"]
                      )}
                    >
                      {values[0]}
                    </span>
                    <span
                      className={clsx(styles["text-gray"], styles["font-bold"])}
                    >
                      {/* eslint-disable-next-line no-unsafe-optional-chaining */}
                      {` /${values[values?.length - 1]}`}
                    </span>
                  </div>
                </Col>
              </Row>
            )}

          {/* Three Value Spread */}
          {(showThreeValueFooter || showThreeValueFooterAsPercentage) &&
            values &&
            values?.length && (
              <div className={styles["gpb-bottom-container"]}>
                <p style={{ marginLeft: "0px" }}>{startingValue}</p>
                <p style={{ marginLeft: "50%" }}>
                  {values[1] / 2}
                  {showThreeValueFooterAsPercentage && "%"}
                </p>
                <p style={{ marginLeft: "auto" }}>
                  {values[1]}
                  {showThreeValueFooterAsPercentage && "%"}
                </p>
              </div>
            )}
        </>
      )}
    </div>
  );
}

GenericProgressBar.propTypes = propTypes;
GenericProgressBar.defaultProps = defaultProps;

export default GenericProgressBar;
